import axios from "axios";
import { composeQueryParams, paramsSerializer } from "../../utils";

const BASE = "/api/admin/ads";
const OPTIONS_PAGE_SIZE = 100;

// Campaign list: paging and search go through composeQueryParams; the ad-specific
// filters travel as top-level params (status[]=active&status[]=scheduled).
const listParams = ({ status, placement, advertiserId, ...paging }, metadata) => ({
  ...composeQueryParams(paging, metadata),
  filters: undefined,
  status,
  placement,
  advertiserId
});

const data = response => response.data;

export const listCampaigns = (payload, metadata) =>
  axios
    .get(`${BASE}/campaigns`, { params: listParams(payload, metadata), paramsSerializer })
    .then(data);
export const readCampaign = id => axios.get(`${BASE}/campaigns/${id}`).then(data);
export const createCampaign = body => axios.post(`${BASE}/campaigns`, body).then(data);
export const updateCampaign = (id, body) => axios.patch(`${BASE}/campaigns/${id}`, body).then(data);
export const pauseCampaign = id => axios.post(`${BASE}/campaigns/${id}/pause`).then(data);
export const unpauseCampaign = id => axios.post(`${BASE}/campaigns/${id}/unpause`).then(data);
export const deleteCampaign = id => axios.delete(`${BASE}/campaigns/${id}`);

export const listAdvertisers = (payload, metadata) =>
  axios
    .get(`${BASE}/advertisers`, {
      params: { ...composeQueryParams(payload, metadata), filters: undefined },
      paramsSerializer
    })
    .then(data);
// Every advertiser as { id, name, creatorId }, paged until the last page so the list is exact.
export const listAdvertiserOptions = async () => {
  const options = [];
  let currentPage = 1;
  let totalPages = 1;
  while (currentPage <= totalPages) {
    const page = await axios
      .get(`${BASE}/advertisers`, {
        params: { currentPage, itemsPerPage: OPTIONS_PAGE_SIZE },
        paramsSerializer
      })
      .then(data);
    options.push(
      ...page.items.map(({ id, name, creator }) => ({ id, name, creatorId: creator?.id ?? null }))
    );
    totalPages = page.totalPages;
    currentPage += 1;
  }
  return options;
};
export const createAdvertiser = body => axios.post(`${BASE}/advertisers`, body).then(data);
export const updateAdvertiser = (id, body) =>
  axios.patch(`${BASE}/advertisers/${id}`, body).then(data);
export const deleteAdvertiser = id => axios.delete(`${BASE}/advertisers/${id}`);

export const readPlacements = () => axios.get(`${BASE}/placements`).then(data);
// Switches a placement on or off; answers with the placement.
export const updatePlacement = (key, body) =>
  axios.patch(`${BASE}/placements/${key}`, body).then(data);

// `crop` ({ x, y, width, height }, integer source pixels) goes with a video only: the API
// crops the video, while an image arrives already cropped. The crop fields precede `file`.
export const uploadCreative = (file, { onProgress = () => {}, crop = null } = {}) => {
  const form = new FormData();
  if (crop) {
    form.append("cropX", String(crop.x));
    form.append("cropY", String(crop.y));
    form.append("cropWidth", String(crop.width));
    form.append("cropHeight", String(crop.height));
  }
  form.append("file", file);
  return axios
    .post(`${BASE}/creatives`, form, {
      onUploadProgress: event => {
        if (event.total) onProgress(Math.round((event.loaded / event.total) * 100));
      }
    })
    .then(data);
};
export const readCreative = id => axios.get(`${BASE}/creatives/${id}`).then(data);
export const deleteCreative = id => axios.delete(`${BASE}/creatives/${id}`);

// Metabase embed for a report. `dashboard` is "overview" or "video-funnel"; only the
// lock params that are set reach the query. Resolves { iframeUrl }; rejects with the
// toRejection shape { status, code, message, fields }.
export const readReportEmbed = (dashboard, { campaignId, advertiserId } = {}) =>
  axios
    .get(`${BASE}/reports/${dashboard}`, {
      params: { campaignId: campaignId || undefined, advertiserId: advertiserId || undefined }
    })
    .then(data)
    .catch(error => Promise.reject(toRejection(error)));

// What a screen receives from a rejected thunk: the HTTP status, the envelope code,
// a message, and the `details` map as { field: message }.
export const toRejection = error => {
  const envelope = error.response?.data?.error || {};
  return {
    status: error.response?.status ?? null,
    code: envelope.code ?? null,
    message: envelope.message || error.message,
    fields: envelope.details || {}
  };
};

// True when a rejection carries field errors, which the form shows under its inputs.
export const hasFields = rejection => Object.keys(rejection?.fields || {}).length > 0;
